<template>
  <div class="rounded-lg border border-gray-200 bg-white p-4 shadow-xs" data-testid="qc-waiting">
    <!--
      The waiting state is the DEFAULT state, not an edge case: the measured
      wait is 45-180 seconds (design-spec §6.1). So a clock and an honest range,
      never a spinner alone and never a fake percentage. The engine's per-check
      count shows only when the engine reports it — the prototype invents one
      from the clock, the product must not.
    -->
    <div class="px-4 py-8 text-center">
      <!-- The prototype's thin grey ring with a blue arc; ProgressSpinner
           cycles four colours, which reads as a status of its own. -->
      <span
        class="inline-block h-4 w-4 animate-spin rounded-full border-2 border-gray-200 border-t-blue-500"
        role="status"
        :aria-label="$t('messages.evaluation.qualityCheck.bar.waiting')"
      ></span>
      <h3 class="mb-1 mt-2.5 text-lg font-semibold text-gray-800" data-testid="qc-waiting-title">
        {{ status === 'queued'
          ? $t('messages.evaluation.qualityCheck.waiting.queued')
          : $t('messages.evaluation.qualityCheck.waiting.processing') }}
      </h3>
      <p class="text-3xl font-light tabular-nums text-gray-800" data-testid="qc-waiting-clock">{{ clock }}</p>
      <p class="mt-1 text-sm text-gray-500" data-testid="qc-waiting-line">
        <span v-if="progressKnown" data-testid="qc-waiting-progress">
          {{ $t('messages.evaluation.qualityCheck.waiting.done', { done: checksDone, total: checksTotal }) }}
        </span>
        {{ $t('messages.evaluation.qualityCheck.waiting.range') }}
      </p>
      <QcNotice tone="info" class="mx-auto mt-3 max-w-2xl text-left">
        {{ $t('messages.evaluation.qualityCheck.waiting.note') }}
      </QcNotice>
    </div>
  </div>
</template>

<script setup>
import { computed } from 'vue'
import QcNotice from './QcNotice.vue'

/**
 * RED-351 rework — `Järjekorras` / `Kontrollime aruannet` (prototype v3).
 */
const props = defineProps({
  /** queued | processing */
  status: { type: String, required: true },
  /** Counted on the server (useReportCheck), so a reload resumes the clock. */
  elapsedSeconds: { type: Number, default: 0 },
  checksDone: { type: Number, default: null },
  checksTotal: { type: Number, default: null },
})

const clock = computed(() => {
  const total = Math.max(0, Math.floor(props.elapsedSeconds))
  const minutes = String(Math.floor(total / 60)).padStart(2, '0')
  const seconds = String(total % 60).padStart(2, '0')
  return `${minutes}:${seconds}`
})

const progressKnown = computed(
  () => props.status === 'processing' && props.checksDone !== null && Boolean(props.checksTotal),
)
</script>
